import { formLabel } from "./techpack_form_contract.js";
import { useEffect, useRef } from "react";
import * as Y from "yjs";
import { v4 as uuidv4 } from "uuid";
import { type BomRow } from "../schema.js";
import { useStudio, useYArray } from "./StudioContext.js";
import { Section, SwatchCell, t, yDelete, yPush, yUpdate } from "./form_primitives.js";
import {
  CANONICAL_MATERIAL_COLUMNS,
  computeLineCost,
  looksLikeClassificationCode,
  studioSwatchKey,
  type MaterialSection,
} from "../generated/shared/types/canonical_techpack_fields.js";

// ---------------------------------------------------------------------------
// 2. BILL OF MATERIALS / INTERLINING / TRIM
//
// All three sections use ONE row shape -- the canonical materials row from
// CANONICAL_TECH_PACK_VERSION_CONTRACT.md §1. Columns are driven entirely by
// CANONICAL_MATERIAL_COLUMNS in shared/types/canonical_techpack_fields.ts, so
// Studio cannot drift from SpecFlow or from the canonical model: adding a
// canonical field adds the column here automatically.
//
// COSTING IS THREE FIELDS, NEVER ONE (contract §1, founder review point 3):
//   unit_price x consumption_qty -> estimated_line_cost
// estimated_line_cost is derived and read-only in the UI, and stays BLANK
// unless both inputs are present -- never partially estimated, never 0 as a
// stand-in for "unknown". Studio's former bare `price` is now `unit_price`.
//
// PRODUCTION CODES vs CLASSIFICATION CODES (founder review point 4):
// fabric_code/color_code here are production/vendor codes ("T-4821"). They
// are NOT the style number's 2-digit fabric_class_code/color_class_code,
// which are Fork-gated identity. A bare 2-digit entry is flagged inline.
//
// SWATCHES: canonical puts swatch_uri ON the row. Studio's live Yjs documents
// store swatches off-row in the images map under `<section>_swatch_<rowid>`.
// Per the 2026-09-17 founder decision the live keys are NOT migrated, so this
// component reads/writes the legacy location and the resolver lifts it onto
// the canonical row at Lock time.
// ---------------------------------------------------------------------------

/** A canonical materials row, with every new sourcing/costing field present
 *  but EMPTY. Nothing is fabricated -- contract §1. */
function mkBomRow(component = ""): BomRow {
  return {
    id: uuidv4(),
    component,
    material: "",
    composition: "",
    gsm: "",
    color_code: "",
    fabric_code: "",
    width: "",
    specification: "",
    price: "",                    // legacy key retained for existing docs
    vendor_article_no: "",
    unit_price: "",
    currency: "",
    consumption_qty: "",
    consumption_unit: "",
    estimated_line_cost: "",
    special_process_notes: "",
  };
}

/** Columns rendered as text/number inputs. `swatch_uri` is handled separately
 *  because Studio stores it off-row (see header). */
const EDITABLE_COLUMNS = CANONICAL_MATERIAL_COLUMNS.filter(
  (c) => c.field !== "swatch_uri",
);

function toNumberOrNull(v: unknown): number | null {
  if (v === null || v === undefined) return null;
  const s = String(v).trim();
  if (s === "") return null;
  const n = Number(s);
  return Number.isFinite(n) ? n : null;
}

function BomTable({ yarray, defaultComponent, lang, images, section }: {
  yarray: Y.Array<BomRow>;
  defaultComponent: string;
  lang: string;
  images: Y.Map<string>;
  section: MaterialSection;
}) {
  const rows = useYArray(yarray);
  const initialized = useRef(false);

  useEffect(() => {
    if (!initialized.current && yarray.length === 0) {
      initialized.current = true;
      yPush(yarray, mkBomRow());
    } else {
      initialized.current = true;
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  function upd(idx: number, field: string, v: string) {
    const r = rows[idx] as unknown as Record<string, unknown>;
    const next: Record<string, unknown> = {
      ...mkBomRow(),
      ...r,
      id: (r.id as string) ?? uuidv4(),
      [field]: v,
    };

    // Keep the stored line cost consistent with its own inputs. Still STORED,
    // not computed on read, so a locked Version's figure never drifts later.
    if (field === "unit_price" || field === "consumption_qty") {
      const cost = computeLineCost(
        toNumberOrNull(next.unit_price),
        toNumberOrNull(next.consumption_qty),
      );
      next.estimated_line_cost = cost === null ? "" : String(cost);
    }

    // Studio's legacy `price` mirrors unit_price so older readers keep working
    // until the field is retired.
    if (field === "unit_price") next.price = v;

    yUpdate(yarray, idx, next as unknown as BomRow);
  }

  return (
    <div className="sf-table-wrap">
      <table className="sf-table sf-table-materials">
        <thead>
          <tr>
            {EDITABLE_COLUMNS.map((col) => (
              <th
                key={col.field}
                className="sf-th"
                title={formLabel(col.field, lang)}
              >
                {formLabel(col.field, lang)}
              </th>
            ))}
            <th className="sf-th sf-th-swatch">{t("col_swatch", lang)}</th>
            <th className="sf-th">×</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((row, idx) => {
            const r = row as unknown as Record<string, string | undefined>;
            return (
              <tr key={row.id ?? idx}>
                {EDITABLE_COLUMNS.map((col) => {
                  // unit_price falls back to the legacy `price` for documents
                  // written before this column existed.
                  const raw =
                    col.field === "unit_price"
                      ? (r.unit_price ?? r.price ?? "")
                      : (r[col.field] ?? "");

                  const suspectCode =
                    (col.field === "fabric_code" || col.field === "color_code") &&
                    looksLikeClassificationCode(raw);

                  return (
                    <td key={col.field} className="sf-td">
                      <input
                        type={col.type === "number" ? "number" : "text"}
                        inputMode={col.type === "number" ? "decimal" : undefined}
                        value={raw}
                        readOnly={col.readOnly}
                        disabled={col.readOnly}
                        title={
                          suspectCode
                            ? formLabel("trim_code_hint", lang)
                            : formLabel(col.field, lang)
                        }
                        onChange={(e) => upd(idx, col.field, e.target.value)}
                        className={
                          "sf-cell" +
                          (col.readOnly ? " sf-cell-derived" : "") +
                          (suspectCode ? " sf-cell-warn" : "")
                        }
                      />
                    </td>
                  );
                })}
                <td className="sf-td sf-td-swatch">
                  <SwatchCell
                    ymap={images}
                    fieldKey={studioSwatchKey(section, row.id ?? String(idx))}
                  />
                </td>
                <td className="sf-td">
                  <button onClick={() => yDelete(yarray, idx)} className="sf-del-btn">×</button>
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
      <button onClick={() => yPush(yarray, mkBomRow())} className="sf-add-btn">
        {t("add_row", lang)}
      </button>
    </div>
  );
}

export function BomSection() {
  const { techPack, languageCode: lang } = useStudio();
  return (
    <Section title={t("s2", lang)}>
      <details className="sf-material-details" open><summary>{formLabel("main_material", lang)}</summary>
        <BomTable yarray={techPack.bom} defaultComponent="Main Body" lang={lang}
        images={techPack.images} section="bom" />
      </details>
      <details className="sf-material-details" open><summary>{t("interlining", lang)}</summary>
        <BomTable yarray={techPack.interlining} defaultComponent="Lining" lang={lang}
        images={techPack.images} section="interlining" />
      </details>
      <details className="sf-material-details sf-material-trim" open><summary>{formLabel("trim", lang)}</summary>
        <BomTable yarray={techPack.trim} defaultComponent="trim" lang={lang}
        images={techPack.images} section="trim" />
      </details>
    </Section>
  );
}
